<template>
  <div class="shipment-map">
    <LoadingIndicator
      class="map-loading-indicator text-white z-1"
      :loading="isDynamicMapLoading || loading"
    />
    <div
      :id="id"
      class="map"
      :class="[
        mapRenderMethodClass,
        {
          'map-is-interactive': dynamic && interactive,
          'static-map': !dynamic,
        },
      ]"
      :style="{
        'background-image': !dynamic ? `url('${staticMap}')` : '',
      }"
      tabindex="-1"
    ></div>
    <div :id="MOUNT_POINT_ID" tabindex="-1" hidden></div>
  </div>
</template>

<script lang="ts" setup>
import 'leaflet/dist/leaflet.css';

import { default as axios, CancelTokenSource } from 'axios';
import type { FeatureCollection } from 'geojson';
import { default as L } from 'leaflet';
import type { PropType, ShallowRef } from 'vue';
import {
  computed,
  h,
  markRaw,
  onBeforeUnmount,
  ref,
  shallowRef,
  toRef,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { Optional, UUID } from '@rygen/rygen-ui/model';
import { notNull, toCssClassName } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useMapDetails } from '@/composables';
import {
  MapRenderMethod,
  Shipment,
  ShipmentEvent,
  ShipmentStop,
  ShipmentStopType,
} from '@/model';

import EventPopup from '@/components/shipment/map/EventPopup.vue';
import StopPopup from '@/components/shipment/map/StopPopup.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';

type ShipmentLike = Pick<Shipment, 'id' | 'stops' | 'getLineItemsForStop'>;

const MOUNT_POINT_ID = 'shipment-map-hidden-mount-point';
const EVENT_POPUP_MAX_WIDTH = 500;

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Object as PropType<ShipmentLike>,
    default: () => new Shipment(),
  },
  staticMap: {
    type: String,
    default: () => '',
  },
  events: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  routeGeoJson: {
    type: Object as PropType<FeatureCollection>,
    default: undefined,
  },
  accessToken: {
    type: String as PropType<UUID>,
    default: () => undefined,
  },
  dynamic: {
    type: Boolean,
    default: () => false,
  },
  interactive: {
    type: Boolean,
    default: () => true,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  shipment,
  staticMap,
  events,
  routeGeoJson,
  accessToken,
  dynamic,
  interactive,
  loading,
} = toRefs(props);

const map: ShallowRef<Optional<L.Map>> = shallowRef();

const { t } = useI18n();
const {
  centerMapOnAmerica,
  fitBoundsOptions,
  generateEventIcon,
  generateHtmlFromComponent,
  generateStopIcon,
  initMap,
  isDynamicMapLoading,
  mapRenderMethod,
  mapRenderMethodClass,
  toLatLngTuple,
  toOptionalLatLngTuple,
} = useMapDetails(map);

// NOTE: Leaflet objects must not enter Vue's deep reactivity system. Their
// internal layer/LatLng graphs get recursively proxied, which retains tens of
// thousands of LatLng/Point objects via @vue/reactivity's reactiveMap (memory
// leak). Each Leaflet assignment below is therefore wrapped in markRaw().
// When this component is migrated to the Composition API, replace these
// data() entries with shallowRef (or plain `let` for fields the template does
// not read) instead of repeating markRaw() at every assignment.
const featureGroup: ShallowRef<Optional<L.FeatureGroup>> = shallowRef();
const appEl = ref(document.querySelector('#app'));
let getMapDetailsCancelToken: Optional<CancelTokenSource> = undefined;

const stops = toRef(() => shipment.value.stops);

const pickDropSequenceNumbers = computed(() => {
  let picks = 0;
  let drops = 0;

  return stops.value.map((stop) => (stop.isPickup ? ++picks : ++drops));
});

const eventsToShow = computed(() => {
  return events.value.filter(
    (e) => !e.isMilestone && !e.isShipmentMilestoneAssociatedEvent,
  );
});

const redrawMap = async () => {
  featureGroup.value?.remove();
  featureGroup.value = markRaw(L.featureGroup());

  if (routeGeoJson?.value) {
    await redrawMapFromGeoJson();
  } else {
    await redrawMapLegacy();
  }
};

const generateStopPickDropSeqNum = (
  shipment: ShipmentLike,
  stop: ShipmentStop,
) => {
  // Use `sequenceNumber` as a fallback if we can't computed a pickDropSeqNum
  // We should always have a sequence number at this point so the -1 is mostly
  // to force typescript to infer `number` type
  const seqNum = stop.sequenceNumber ?? -1;
  const stopIndex = shipment.stops.findIndex((s) => s.id === stop.id);
  const pickDropSeqNum: Optional<number> =
    pickDropSequenceNumbers.value[stopIndex];
  const prefix =
    stop.stopType === ShipmentStopType.PICKUP
      ? t('shipments.pickInitial')
      : t('shipments.dropInitial');

  return pickDropSeqNum !== undefined
    ? `${prefix}${pickDropSeqNum}`
    : `${seqNum}`;
};

const generateBasicStopPopupHtml = (stop?: ShipmentStop) => {
  return stop
    ? `<p class="stop-popup basic">${stop.location.cityState}</p>`
    : '';
};

const generateRichStopPopupHtml = (
  shipment: ShipmentLike,
  stop?: ShipmentStop,
  isMissingCoords = false,
) => {
  return stop
    ? generateHtmlFromComponent(
        {
          render: () =>
            h(StopPopup, {
              id: `stop-${stop.sequenceNumber}`,
              stop: stop,
              lineItems: shipment.getLineItemsForStop(stop),
              stopLabel: generateStopPickDropSeqNum(shipment, stop),
              isMissingCoords,
            }),
        },
        `#${MOUNT_POINT_ID}`,
      )
    : '';
};

const generateEventPopupHtml = (
  event: ShipmentEvent,
  isMissingCoords = false,
) => {
  return generateHtmlFromComponent(
    {
      render: () =>
        h(EventPopup, {
          event,
          isMissingCoords,
        }),
    },
    `#${MOUNT_POINT_ID}`,
  );
};

const redrawMapFromGeoJson = async () => {
  if (!map.value || !featureGroup.value || !routeGeoJson?.value) {
    return;
  }

  mapRenderMethod.value = MapRenderMethod.GEOJSON;

  try {
    const markersWithPopups: L.Marker[] = [];

    const stopCoords = new Set<string>();
    for (const feature of routeGeoJson.value.features) {
      if (
        feature.properties?.['source-type'] === 'STOP' &&
        feature.geometry?.type === 'Point'
      ) {
        const coords = (feature.geometry as GeoJSON.Point).coordinates;
        stopCoords.add(`${coords[0]},${coords[1]}`);
      }
    }

    const geoJsonLayer = L.geoJSON(routeGeoJson.value, {
      filter: (feature) => {
        if (
          feature.properties?.['source-type'] === 'EVENT' &&
          feature.geometry?.type === 'Point'
        ) {
          const coords = (feature.geometry as GeoJSON.Point).coordinates;
          return !stopCoords.has(`${coords[0]},${coords[1]}`);
        }
        return true;
      },
      style: (feature) => {
        if (feature?.geometry?.type === 'LineString') {
          const color = Shipment.colorOptions[0];
          return { color, weight: 4 };
        }
        return {};
      },
      pointToLayer: (feature, latlng) => {
        const props = feature.properties || {};
        const sourceId: number = props['source-id'];
        const sourceType: string = props['source-type'];
        const sourceDetail: string = props['source-detail'];

        if (sourceType === 'STOP') {
          const featureGroup = L.featureGroup();
          const stop = stops.value.find((s) => s.id === sourceId);

          const icon = generateStopIcon(sourceDetail);

          const basicPopupContent = generateBasicStopPopupHtml(stop);
          const markerWithBasicPopup = L.marker(latlng, { icon: icon })
            .addTo(featureGroup)
            .bindPopup(basicPopupContent, {
              autoClose: false,
              closeButton: false,
              closeOnClick: false,
              closeOnEscapeKey: false,
            });
          markersWithPopups.push(markerWithBasicPopup);

          const richPopupContent = generateRichStopPopupHtml(
            shipment.value,
            stop,
          );
          L.marker(latlng, { icon })
            .addTo(featureGroup)
            .bindPopup(richPopupContent, {
              autoClose: false,
              closeButton: false,
              closeOnClick: false,
              closeOnEscapeKey: false,
              keepInView: true,
            })
            .on('mouseover', (event) => event.target.openPopup())
            .on('mouseout', (event) => event.target.closePopup());

          return featureGroup;
        } else {
          const event = eventsToShow.value.find((e) => e.id === sourceId);

          const icon = generateEventIcon(sourceDetail);
          const popupContent = event ? generateEventPopupHtml(event) : '';
          return L.marker(latlng, { icon, riseOnHover: true })
            .bindPopup(popupContent, {
              keepInView: true,
              maxWidth: EVENT_POPUP_MAX_WIDTH,
            })
            .on('mouseover', (event) => event.target.openPopup())
            .on('mouseout', (event) => event.target.closePopup());
        }
      },
    });

    geoJsonLayer.addTo(featureGroup.value);
    map.value.fitBounds(featureGroup.value.getBounds(), fitBoundsOptions);
    featureGroup.value.addTo(map.value);
    markersWithPopups.forEach((marker) => marker.openPopup());
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(e);
    await redrawMapLegacy();
  }
};

const redrawMapLegacy = async () => {
  if (!map.value || !featureGroup.value || !stops.value) {
    return;
  }

  mapRenderMethod.value = MapRenderMethod.LEGACY;

  getMapDetailsCancelToken?.cancel();
  getMapDetailsCancelToken = axios.CancelToken.source();

  const validFeatureGroup = featureGroup.value;
  const destinationIndex = stops.value.length - 1;
  const eventLayer = L.layerGroup();

  try {
    const mapDetails =
      accessToken?.value && shipment.value.id
        ? await api.carrierPortal.getMapDetails(
            shipment.value.id,
            stops.value,
            events.value,
            accessToken.value,
            getMapDetailsCancelToken,
          )
        : await api.route.getMapDetails(
            stops.value,
            events.value,
            getMapDetailsCancelToken,
          );

    const markersWithPopups: L.Marker[] = [];
    mapDetails.stopCoordinates.forEach((point, i) => {
      // If the API failed to geocode the stop, `point` will be `undefined`
      const isMissingCoords = !point;
      const latLngTuple = toLatLngTuple(point);
      const stop = stops.value[i];

      const classNames = ['stop'];
      classNames.push(
        stop.stopType === ShipmentStopType.PICKUP ? 'pickup' : 'dropoff',
      );
      if (i === 0) {
        classNames.push('origin');
      } else if (i === destinationIndex) {
        classNames.push('destination');
      }

      if (isMissingCoords) {
        const errorClassName = stop.location.locationName
          ? 'has-geocode-error'
          : 'missing-location';
        classNames.push(errorClassName);
      }

      const icon = generateStopIcon(classNames.join(' '));

      const basicPopupContent = generateBasicStopPopupHtml(stop);
      const markerWithBasicPopup = L.marker(latLngTuple, { icon: icon })
        .addTo(validFeatureGroup)
        .bindPopup(basicPopupContent, {
          autoClose: false,
          closeButton: false,
          closeOnClick: false,
          closeOnEscapeKey: false,
        });
      markersWithPopups.push(markerWithBasicPopup);

      const richPopupContent = generateRichStopPopupHtml(shipment.value, stop);
      L.marker(latLngTuple, { icon: icon })
        .addTo(validFeatureGroup)
        .bindPopup(richPopupContent, {
          autoClose: false,
          closeButton: false,
          closeOnClick: false,
          closeOnEscapeKey: false,
          keepInView: true,
        })
        .on('mouseover', (event) => event.target.openPopup())
        .on('mouseout', (event) => event.target.closePopup());
    });

    mapDetails.legCoordinates.map((coords) => {
      const mappedPoints = coords
        .map((c) => toOptionalLatLngTuple(c))
        .filter(notNull);

      L.polyline(mappedPoints, {
        weight: 4,
        color: Shipment.colorOptions[0],
      }).addTo(validFeatureGroup);
    });

    mapDetails.eventCoordinates.map((coords, index) => {
      // If the API failed to geocode the stop, `coords` will be `undefined`
      const isMissingCoords = !coords;
      const latLng = toLatLngTuple(coords);
      const event = eventsToShow.value[index];

      const classNames = [
        'event',
        toCssClassName(event.eventType),
        toCssClassName(event.severity),
      ];
      if (isMissingCoords) {
        classNames.push('has-geocode-error');
      }

      const icon = generateEventIcon(classNames.join(' '));
      const popupContent = generateEventPopupHtml(event, isMissingCoords);
      L.marker(latLng, { icon: icon, riseOnHover: true })
        .addTo(eventLayer)
        .bindPopup(popupContent, {
          keepInView: true,
          maxWidth: EVENT_POPUP_MAX_WIDTH,
        })
        .on('mouseover', (event) => event.target.openPopup())
        .on('mouseout', (event) => event.target.closePopup());
    });

    map.value.fitBounds(validFeatureGroup.getBounds(), fitBoundsOptions);
    validFeatureGroup.addTo(map.value);
    markersWithPopups.forEach((marker) => marker.openPopup());
    eventLayer.addTo(map.value);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error redrawing legacy map', e);
    centerMapOnAmerica();
  }
};

watch(stops, () => {
  if (!routeGeoJson?.value) {
    redrawMap();
  }
});

watch(dynamic, (newVal) => {
  if (newVal) {
    initMap(id.value);
    redrawMap();
  }
});

watch(
  () => routeGeoJson?.value,
  () => {
    if (map.value) {
      redrawMap();
    }
  },
);

watch(interactive, (newVal) => {
  if (newVal) {
    map.value?.dragging.enable();
    map.value?.scrollWheelZoom.enable();
    map.value?.doubleClickZoom.enable();
  } else {
    map.value?.dragging.disable();
    map.value?.scrollWheelZoom.disable();
    map.value?.doubleClickZoom.disable();
  }
});

watch(
  () => [staticMap.value, map.value],
  ([newStaticMap, newMapObj]) => {
    const isInitialized = !!newStaticMap || !!newMapObj;
    appEl.value?.classList.toggle('with-map', isInitialized);
  },
);

onBeforeUnmount(() => {
  map.value?.off();
  map.value?.remove();
  appEl.value?.classList?.remove('with-map');
});
</script>

<style>
.shipment-map {
  background: var(--map-background);
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 0;
  right: 0;
  height: 500px;
  z-index: 0;

  .map-loading-indicator {
    position: absolute;
    font-size: 3rem;
    top: 240px;
    left: calc((100vw - var(--app-nav-width)) / 2 - (3rem / 2));
  }

  .map {
    background: inherit;
    width: 100%;
    height: 100%;

    &.static-map {
      background-size: 100%;
      background-position: center;
    }

    .leaflet-popup {
      &:has(.stop-popup.basic) {
        .leaflet-popup-content-wrapper {
          .leaflet-popup-content {
            .stop-popup.basic {
              background-color: var(--p-gray-500);
              color: var(--p-gray-100);
              border-radius: var(--p-border-radius-sm);
              line-height: 1;
              margin: 0;
            }
          }
        }

        .leaflet-popup-tip {
          background-color: var(--p-gray-500);
          pointer-events: none;
        }
      }

      &:has(.event-popup) {
        &:has(.body-row) {
          .leaflet-popup-tip {
            background-color: var(
              --base-shipment-timeline-item-body-row-background-color
            );
          }
        }

        .leaflet-popup-content-wrapper {
          .leaflet-popup-content {
            .event-popup {
              border-left: 4px solid;

              &.normal {
                border-left-color: var(--info-color);
              }

              &.warning {
                border-left-color: var(--warning-color);
              }

              &.critical {
                border-left-color: var(--danger-color);
              }
            }
          }
        }

        .leaflet-popup-tip {
          background-color: var(--p-card-background);
        }
      }

      .leaflet-popup-content-wrapper {
        background: transparent;
        box-shadow: none;

        .leaflet-popup-content {
          margin: 0;
        }
      }

      .leaflet-popup-tip {
        background: var(--p-card-background);
      }
    }

    .leaflet-marker-icon {
      &.stop {
        &:is(.origin, .destination) {
          .pi-map-marker {
            font-size: 28px;
          }
        }

        &.has-geocode-error {
          --stop-type-accent-color: var(--danger-color);
        }

        .pi-map-marker {
          font-size: 16px;
          color: var(--stop-type-accent-color);
        }
      }

      &.event {
        background: transparent;
        border: none;

        &.normal {
          color: var(--p-gray-100);
        }

        &.warning {
          color: var(--warning-color);
        }

        &.critical {
          color: var(--danger-color);
        }
      }
    }
  }
}
</style>
